/* =============================================================================
   CORE / SUPPLIERS — Lieferanten + Einkaufseinheiten (Phase E4a)
   =============================================================================
   PURE Helfer rund um Lieferanten je Zutat (ingredient_suppliers). Single-Source der
   Einkaufs-Mathematik (Spiegel der SQL-RPCs aus Migration phase_121):

     raw_per_base = purchase_price / unit_qty_base   (€ je Basiseinheit, OHNE Yield)
     base_menge   = qty × unit_qty_base              (Einkaufseinheit → Basiseinheiten)

   KERN-INTEGRATIONSREGEL: der Hauptlieferant speist ingredients.purchase_price (= sein
   purchase_price) + purchase_qty (= sein unit_qty_base) + purchase_unit (Name). So rechnen
   Food-Cost (phase_117) und Lagerwert (phase_120) unverändert weiter. „Genau ein Haupt-
   lieferant" wird DB-seitig per Partial-Unique erzwungen; hier die UI-/Test-Spiegelung.
============================================================================ */

/**
 * € je Basiseinheit eines Lieferanten-Eintrags (roh, OHNE Yield).
 * @param {{purchasePrice, unitQtyBase}} s
 * @returns {number|null} null bei fehlendem/≤0-Preis oder ≤0-Umrechnung
 */
export function rawPerBaseFromSupplier(s) {
  if (!s) return null;
  const pp = Number(s.purchasePrice);
  const uq = Number(s.unitQtyBase);
  if (!(pp > 0) || !(uq > 0)) return null;
  return pp / uq;
}

/**
 * Einkaufsmenge → Basiseinheiten (qty × Basiseinheiten-je-Einkaufseinheit).
 * Für den Wareneingang in Einkaufseinheit (z. B. 2 Karton × 6000 g = 12000 g).
 * @returns {number|null} null bei ungültiger Menge/Umrechnung (≤0)
 */
export function purchaseUnitToBase(qty, unitQtyBase) {
  const q = Number(qty);
  const u = Number(unitQtyBase);
  if (!isFinite(q) || q == null) return null;
  if (!(u > 0)) return null;
  return q * u;
}

/** Hauptlieferant aus einer Liste (is_primary). null, wenn keiner. */
export function primarySupplier(list) {
  return (Array.isArray(list) ? list : []).find(s => s && s.isPrimary) || null;
}

/**
 * Liste so normalisieren, dass GENAU ein Hauptlieferant gesetzt ist (UI-Spiegel der
 * DB-Exklusivität): der mit `chosenId` wird Haupt, alle anderen verlieren es. Ist keine
 * chosenId angegeben/gefunden und es gibt keinen Haupt, wird der erste Eintrag Haupt.
 * Reine Funktion (neue Objekte), mutiert die Eingabe nicht.
 */
export function ensureSinglePrimary(list, chosenId) {
  const arr = (Array.isArray(list) ? list : []).map(s => ({ ...s }));
  if (!arr.length) return arr;
  let target = chosenId != null ? arr.find(s => s.id === chosenId) : null;
  if (!target) target = arr.find(s => s.isPrimary) || arr[0];
  for (const s of arr) s.isPrimary = (s === target);
  return arr;
}

/**
 * E4a-Hotfix — die abgeleiteten Einkaufsfelder aus einem Save-Payload entfernen, wenn ein
 * Hauptlieferant existiert: purchase_price/-qty/-unit kommen dann vom Hauptlieferanten
 * (Sync-Trigger) und dürfen NICHT vom manuellen „Einkauf"-Formular zurückgeschrieben werden
 * (sonst gg-settings-full-put-clobber). Ohne Hauptlieferant unverändert (manueller Fallback).
 * Reine Funktion (neues Objekt).
 */
export function omitDerivedPurchase(obj, hasPrimary) {
  const out = { ...(obj || {}) };
  if (hasPrimary) {
    delete out.purchasePrice;
    delete out.purchaseQty;
    delete out.purchaseUnit;
  }
  return out;
}

/**
 * Was der Hauptlieferant in ingredients.purchase_* speist (Vorschau/Sync-Spiegel).
 * @returns {{purchasePrice, purchaseQty, purchaseUnit}|null} null ohne Hauptlieferant
 */
export function effectivePurchaseFromSuppliers(list) {
  const p = primarySupplier(list);
  if (!p) return null;
  return {
    purchasePrice: p.purchasePrice != null ? Number(p.purchasePrice) : null,
    purchaseQty: p.unitQtyBase != null ? Number(p.unitQtyBase) : null, // ingredients.purchase_qty = unit_qty_base
    purchaseUnit: p.purchaseUnit != null ? p.purchaseUnit : null
  };
}
